import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BaseServiceResponse } from '../../models/api-calls/base-service-response';
import { AccountDto } from '../../models/dtos/account-dto';
import { unconfirmedResponse } from '../../models/forms/auth-form-state';
import { SessionService } from '../account-service/session.service';

// Only local router paths are eligible. Never accept an origin, auxiliary outlet or auth callback.
export function safeReturnUrl(value: string | null | undefined): string | undefined {
  if (!value || value.length > 4000 || !value.startsWith('/') || value.startsWith('//')) {
    return undefined;
  }

  try {
    let decoded = value;
    for (let i = 0; i < 3; i++) {
      decoded = decodeURIComponent(decoded);
      if (decoded.startsWith('//') || /[\\\u0000-\u001f\u007f]/.test(decoded)) {
        return undefined;
      }
    }
    const path = decoded.split(/[?#]/, 1)[0];
    if (/[();]/.test(path) || /^\/(login|register)(\/|$)/i.test(path)) {
      return undefined;
    }
    return value;
  } catch {
    return undefined;
  }
}

export type RegistrationCompletion = 'created' | 'reconnect';

@Injectable({ providedIn: 'root' })
export class RedirectService {
  // Private variables

  private readonly storageKey = 'squarecontrol.clarity.return-url';
  private destination?: string;

  // Public properties

  // Constructors

  constructor(
    private readonly router: Router,
    private readonly session: SessionService,
    @Inject(DOCUMENT) private readonly document: Document
  ) {}

  // Public methods

  public get queryParams(): { returnUrl?: string } {
    return { returnUrl: this.read() };
  }

  public remember(value: string | null | undefined): void {
    this.destination = safeReturnUrl(value);
    try {
      const storage = this.document.defaultView?.sessionStorage;
      if (this.destination) {
        storage?.setItem(this.storageKey, this.destination);
      } else {
        storage?.removeItem(this.storageKey);
      }
    } catch {
      // In-memory navigation still works when browser storage is disabled.
    }
  }

  public clear(): void {
    this.remember(undefined);
  }

  public async afterLogin(): Promise<void> {
    const destination = this.read() || '/dashboard';
    const navigated = await this.router.navigateByUrl(destination, { replaceUrl: true });

    if (navigated && this.read() === destination)
      this.clear();
  }

  public async completeSignIn(response: BaseServiceResponse<AccountDto>): Promise<boolean> {
    if (response.succeeded && response.payload && this.session.account()?.id === response.payload.id) {
      await this.afterLogin();
      return true;
    }
    if (this.session.status() === 'unavailable' && this.session.currentToken) {
      await this.router.navigate(['/login/session'], { queryParams: this.queryParams, replaceUrl: true });
      return true;
    }
    return false;
  }

  public rememberRegistration(): void {
    const destination = this.read();
    const path = destination ? decodeURIComponent(destination.split(/[?#]/, 1)[0]) : '';

    if (destination && (/^\/(household\/invite|invite)\//i.test(path) ||
      /^\/dashboard\/clubs\/create\/?$/i.test(path) || /^\/onboarding\/?$/i.test(path))) {
      this.remember(destination);
      return;
    }

    const onboarding = destination ? `/onboarding?returnUrl=${encodeURIComponent(destination)}` : '/onboarding';

    this.remember(safeReturnUrl(onboarding) || '/onboarding');
  }

  public async completeRegistration(response: BaseServiceResponse<AccountDto>): Promise<RegistrationCompletion | undefined> {
    const created = response.succeeded && !!response.payload && this.session.account()?.id === response.payload.id;
    const reconnect = unconfirmedResponse(response) && this.session.status() === 'unavailable' && !!this.session.currentToken;

    if (!created && !reconnect)
      return undefined;

    this.rememberRegistration();
    await this.continueRegistration();

    // A failed navigation must not turn a confirmed signup into another account-creation attempt.
    return created ? 'created' : 'reconnect';
  }

  public async continueRegistration(): Promise<boolean> {
    try {
      if (this.session.status() === 'unavailable' && this.session.currentToken) {
        return await this.router.navigate(['/login/session'], { queryParams: this.queryParams, replaceUrl: true });
      }

      const destination = this.read() || '/onboarding';
      const navigated = await this.router.navigateByUrl(destination, { replaceUrl: true });

      if (navigated && this.read() === destination)
        this.clear();

      return navigated;
    } catch {
      return false;
    }
  }

  // Private methods

  private read(): string | undefined {
    if (this.destination) {
      return this.destination;
    }
    try {
      return safeReturnUrl(this.document.defaultView?.sessionStorage.getItem(this.storageKey));
    } catch {
      return undefined;
    }
  }
}
